import { useId } from 'react';
import { ExpressionInput } from 'calc-controls';

export interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: string;
  value: number | '';
  onChange?: (value: number, raw: string) => void;
  error?: string;
  hint?: string;
  /** Decimal places for display and rounding (passed to calc-controls). Default: 4 */
  decimals?: number;
  /** Unit suffix rendered by calc-controls (e.g. "mg", "ng·h/mL") */
  unit?: string;
}

/**
 * Numeric input backed by calc-controls `ExpressionInput`.
 * Accepts typed values and math expressions (e.g. "+25", "*1.2", "(x+5)*2").
 * Inline mode keeps the host app's stacked label/control layout.
 */
export function NumberInput({
  label,
  value,
  onChange,
  error,
  hint,
  id,
  min,
  max,
  className = '',
  decimals = 4,
  unit,
}: NumberInputProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  const numericValue = value === '' || Number.isNaN(value) ? 0 : value;
  const numericMin = typeof min === 'number' ? min : undefined;
  const numericMax = typeof max === 'number' ? max : undefined;

  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={`number-input-wrapper ${className}`.trim()}>
      {label ? (
        <label className="field-label" htmlFor={fieldId}>
          {label}
        </label>
      ) : null}
      <ExpressionInput
        id={fieldId}
        value={numericValue}
        onChange={(v) => onChange?.(v, String(v))}
        min={numericMin}
        max={numericMax}
        decimals={decimals}
        unit={unit}
        inline
        aria-label={label}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {error ? (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? <p id={hintId} className="field-hint">{hint}</p> : null}
    </div>
  );
}
